import {
  Activity,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";
import { ActivityType } from "../../../domain/ActivityType";
import {
  AdditionalEvidenceBody,
  AssignActivityBody,
  AttachmentActivityBody,
  AttachmentBodyProps,
  ChangeActivityBody,
  CloseActivityBody,
  DateActivityBody,
  MessageActivityBody,
  NoteActivityBody,
  SubmittedEvidenceActivityBody,
} from "./ExpansionActivityFeedTypes";
import { ScopeApplication } from "../../../domain/ScopeApplication";
import ReactMarkdown from "react-markdown";
import { scopeExpansionService } from "../../../services";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { useSelector } from "react-redux";
import { authSelector } from "../../../redux/selectors";
import { fieldLabels } from "../../scopes/fieldLabels";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";

export const buildDefinitions = (
  application: ScopeApplication | null,
  onClick: (entity: Activity | null) => void,
  canAddEvidence: boolean
) => {
  if (!application) {
    return [];
  }

  const assignApplication: ActivityFeedItemDefinition<AssignActivityBody> = {
    activityType: ActivityType.ScopeApplicationAssignment,
    Body: ({ body }) => (
      <>
        <div>
          <b>Assigned to: </b> {body.AssignToDisplayName}{" "}
        </div>
        {body.Message ? (
          <>
            <div>
              <b>Message: </b>{" "}
              <div className="ActivityNote"> {body.Message}</div>
            </div>
          </>
        ) : null}
      </>
    ),
    options: [],
  };

  const note: ActivityFeedItemDefinition<NoteActivityBody> = {
    activityType: ActivityType.ScopeNote,
    Body: ({ body }) => (
      <div className="ActivityNote">
        {<ReactMarkdown>{body.Note}</ReactMarkdown>}
      </div>
    ),
    options: [],
  };

  const AttachmentBody: React.FC<AttachmentBodyProps> = ({
    bodyProps,
    getBlob,
  }) => {
    const { body } = bodyProps;

    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.Documents
            ? body.Documents.map((document) => (
                <li key={document.BlobId} style={{ listStyleType: "none" }}>
                  <DownloadLink
                    fileName={document.FileName}
                    getBlob={getBlob(document.BlobId)}
                  />
                </li>
              ))
            : null}
        </ul>
        {body.DocumentType && (
          <div>
            <b>Document type:</b> {body.DocumentType}
          </div>
        )}
        {body.Note && (
          <div className="ActivityNote">
            <b>Note:</b> {body.Note}
          </div>
        )}
      </>
    );
  };

  const documentActivity: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.ScopeDocument,
    Body: ({ body, activity }) => {
      const { token } = useSelector(authSelector);
      const buildGetBlob = (blobId: string) => () =>
        application &&
        scopeExpansionService.downloadActivityDocument(
          application.organisationID.toString(),
          application.id,
          blobId,
          { token }
        );
      return (
        <AttachmentBody getBlob={buildGetBlob} bodyProps={{ activity, body }} />
      );
    },
  };

  const scopeUpdate: ActivityFeedItemDefinition<ChangeActivityBody> = {
    activityType: ActivityType.ScopeChanged,
    Body: ({ body }) => (
      <>
        <div>
        <b>Updated: </b>
        {body.Changes.map((item) => `"${fieldLabels[item.Field]}"`).join(",")}
        </div>
        {body.Message ? (
          <>
            <div>
              <b>Message: </b>{" "}
              <div style={{whiteSpace: "pre-wrap"}}> {body.Message}</div>
            </div>
          </>
        ) : null}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick,
      },
    ],
  };

  const date: ActivityFeedItemDefinition<DateActivityBody> = {
    activityType: ActivityType.ScopeDate,
    Body: ({ body }) => (
      <>
        <div>
          <b>Date: </b>
          {formatUtcDate(body.Date, standardDateFormats.DATE)}
        </div>

        <div>
          <b>Message: </b>{" "}
          <div className="ActiviyNote">
            {<ReactMarkdown>{body.Note}</ReactMarkdown>}
          </div>{" "}
        </div>
      </>
    ),
    options: [],
  };

  const closeActivity: ActivityFeedItemDefinition<CloseActivityBody> = {
    activityType: ActivityType.ScopeClosed,
    Body: ({ body, activity }) => {
      const { token } = useSelector(authSelector);
      const buildGetBlob = (blobId: string) => () =>
        application &&
        scopeExpansionService.downloadCloseDocument(
          application.organisationID.toString(),
          application.id,
          blobId,
          { token }
        );
      return (
        <AttachmentBody
          getBlob={buildGetBlob}
          bodyProps={{
            activity,
            body: { Note: body.Note, Documents: [body.Letter] },
          }}
        />
      );
    },
  };

  const additionalEvidenceRequest: ActivityFeedItemDefinition<AdditionalEvidenceBody> = {
    activityType: ActivityType.ScopeAdditionalEvidenceRequest,
    Body: ({ body }) => (
      <>
        <b>Title: </b> {body.Title} <br />
        <b>Due Date: </b>
        {formatUtcDate(body.DueDate, standardDateFormats.DATE)} <br />
        {body.StatementsOfAssurance ? (
          <>
            <b>Statements of Assurance: </b>
            {body.StatementsOfAssurance.map((item) => `${item.Name}`).join(
              ", "
            )}
            <br />
          </>
        ) : null}
        <b>Other evidence requested?: </b>
        {body.IsOtherEvidenceRequired ? "Yes" : "No"} <br />
        <b>Attachments Allowed?: </b>
        {body.AllowAttachments ? "Yes" : "No"} <br />
      </>
    ),
    options: canAddEvidence
      ? [
          {
            label: "Add additional evidence",
            onClick,
          },
        ]
      : [],
  };

  const messageOfqual: ActivityFeedItemDefinition<MessageActivityBody> = {
    activityType: ActivityType.ScopeMessageOfqual,
    Body: ({ body }) => (
      <div className="ActivityNote">
        {<ReactMarkdown>{body.Message}</ReactMarkdown>}
      </div>
    ),
    options: [],
  };

  const messageAo: ActivityFeedItemDefinition<MessageActivityBody> = {
    activityType: ActivityType.ScopeMessageAo,
    Body: ({ body }) => (
      <div className="ActivityNote">
        {<ReactMarkdown>{body.Message}</ReactMarkdown>}
      </div>
    ),
    options: [],
    align: "left",
  };

  const withdrawn: ActivityFeedItemDefinition<NoteActivityBody> = {
    activityType: ActivityType.ScopeWithdrawn,
    Body: ({ body }) => (
      <div className="ActivityNote">
        {<ReactMarkdown>{body.Note}</ReactMarkdown>}
      </div>
    ),
    options: [],
  };

  const submittedEvidence: ActivityFeedItemDefinition<SubmittedEvidenceActivityBody> = {
    activityType: ActivityType.ScopeAdditionalEvidenceSubmitted,
    Body: ({ body, activity }) => (
      <>
        <b>Title: </b>
        {body.Title}
        <br />
        <b>Due date: </b>
        {formatUtcDate(body.DueDate, standardDateFormats.DATE)}
        <br />
        <b>On time?: </b>
        {body.DueDate > activity.createdDate ? "Yes" : "No"}
      </>
    ),
    options: [
      {
        label: "View evidence",
        onClick,
      },
    ],
  };

  return [
    assignApplication,
    note,
    documentActivity,
    scopeUpdate,
    date,
    closeActivity,
    additionalEvidenceRequest,
    messageOfqual,
    messageAo,
    withdrawn,
    submittedEvidence,
  ];
};
